import { Form, Formik } from "formik";
import { MailCheck } from "lucide-react";
import { useState } from "react";
import { FiArrowLeft, FiArrowRight, FiAtSign } from "react-icons/fi";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { requestPasswordReset } from "@/features/auth/api";

const schema = Yup.object({
  email: Yup.string().email("Enter a valid email").required("Email is required"),
});

interface ForgotValues {
  email: string;
}

/**
 * Request-a-reset form. Submitting emails a reset link; the backend response is
 * deliberately generic (it never reveals whether the address is registered), so
 * on any successful call we show the same "check your inbox" confirmation rather
 * than branching on existence.
 */
export function ForgotPasswordForm() {
  const [sentTo, setSentTo] = useState<string | null>(null);

  async function handleSubmit(values: ForgotValues) {
    try {
      await requestPasswordReset(values.email);
    } catch {
      // Swallow errors too: surfacing a failure here would leak that the address
      // exists (or not). The confirmation is intentionally unconditional.
    }
    setSentTo(values.email);
  }

  if (sentTo) {
    return (
      <>
        <Card className="gap-0 p-6 sm:p-10">
          <div className="mb-8 flex flex-col items-center text-center">
            <div className="bg-primary/10 text-primary mb-4 flex size-12 items-center justify-center rounded-full">
              <MailCheck className="size-6" />
            </div>
            <h1 className="font-heading text-foreground mb-1 text-2xl font-bold">Check your email</h1>
            <p className="text-muted-foreground text-sm">
              If an account exists for{" "}
              <span className="text-foreground font-medium">{sentTo}</span>, we&apos;ve sent a link
              to reset your password. The link expires shortly.
            </p>
          </div>
          <Link to="/login" className="mx-auto">
            <Button variant="outline" className="h-11 gap-2">
              <FiArrowLeft className="size-4" />
              Back to sign in
            </Button>
          </Link>
        </Card>
      </>
    );
  }

  return (
    <>
      <Card className="gap-0 p-6 sm:p-10">
        <div className="mb-10">
          <h1 className="font-heading text-foreground mb-2 text-2xl font-bold">
            Reset your password
          </h1>
          <p className="text-muted-foreground">
            Enter your email and we&apos;ll send you a link to set a new password.
          </p>
        </div>

        <Formik<ForgotValues>
          initialValues={{ email: "" }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-6">
              <FormField
                name="email"
                label="Work Email"
                type="email"
                autoComplete="email"
                placeholder="name@cpafirm.com"
                icon={<FiAtSign className="size-4" />}
                className="h-11"
              />
              <Button type="submit" disabled={isSubmitting} className="h-11 gap-2">
                {isSubmitting ? "Sending..." : "Send reset link"}
                <FiArrowRight className="size-4" />
              </Button>
            </Form>
          )}
        </Formik>
      </Card>

      <p className="text-muted-foreground mt-8 text-center text-sm">
        Remembered it?{" "}
        <Link to="/login" className="text-primary hover:text-primary/80 font-medium transition-colors">
          Back to sign in
        </Link>
      </p>
    </>
  );
}
